import { useCallback, useEffect, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InlineWarning } from "@/components/common/Primitives";
import { api, apiError } from "@/lib/api";
import { dateOnly, eur, km } from "@/lib/format";

const Row = ({ label, value, note, strong, tone }) => (
  <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-3 py-1.5">
    <div className="min-w-0">
      <div className={`text-[12px] ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {label}
      </div>
      {note && <div className="text-[10px] leading-tight text-slate-500">{note}</div>}
    </div>
    <div
      className={`mono shrink-0 text-right text-[12px] ${
        tone === "positive"
          ? "font-semibold text-emerald-700"
          : strong
            ? "font-semibold text-slate-900"
            : "text-slate-800"
      }`}
    >
      {value}
    </div>
  </div>
);

export const BpmOverrideDialog = ({ open, onOpenChange, row, onApplied }) => {
  const listingId = row?.listing_id;
  const head = row?.headline || {};
  const bpm = row?.bpm || {};
  const isManual = head.bpm_source === "MANUAL" || bpm.manual_override === true;
  const automatic = bpm.automatic_bpm ?? head.bpm_automatic ?? (isManual ? null : head.bpm);

  const [amount, setAmount] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmMixed, setConfirmMixed] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!listingId) return;
    setBusy("preview");
    try {
      const { data } = await api.post(`/listings/${listingId}/bpm-override/preview`, {
        amount: null,
      });
      setPreview(data);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  }, [listingId]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setConfirmMixed(false);
    setPreview(null);
    const start = head.bpm ?? bpm.bpm_estimate ?? null;
    setAmount(start === null || start === undefined ? "" : String(Math.round(start)));
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listingId]);

  const value = Number(String(amount).replace(/[^0-9,.-]/g, "").replace(",", "."));
  const valid = amount !== "" && !Number.isNaN(value) && value >= 0;

  const apply = async (scope) => {
    if (!valid) {
      setError("Vul een geldig BPM-bedrag in euro's in.");
      return;
    }
    setBusy(scope);
    setError("");
    try {
      const { data } = await api.post(`/listings/${listingId}/bpm-override`, {
        amount: value,
        scope,
        note: null,
        confirm_mixed_registrations: scope === "GROUP" ? confirmMixed : true,
      });
      onApplied?.(data);
      onOpenChange(false);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  };

  const restore = async (scope) => {
    setBusy(`restore-${scope}`);
    setError("");
    try {
      const { data } = await api.delete(`/listings/${listingId}/bpm-override?scope=${scope}`);
      onApplied?.(data);
      onOpenChange(false);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  };

  const count = preview?.affected_count ?? 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="bpm-override-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-base">BPM handmatig aanpassen</DialogTitle>
        </DialogHeader>
        <div className="max-h-[72vh] space-y-3 overflow-y-auto">
          {error && (
            <InlineWarning tone="danger" testid="bpm-override-error">
              {error}
            </InlineWarning>
          )}

          <div className="border border-slate-200">
            <Row
              label="Automatisch berekende BPM (CO₂)"
              note={bpm.automatic?.status_label || (isManual ? null : bpm.status_label)}
              value={eur(automatic, { fallback: "Niet te bepalen" })}
            />
            {isManual && (
              <Row label="Huidige handmatige BPM" value={eur(head.bpm_manual ?? bpm.manual_bpm)} />
            )}
            <Row label="Gebruikte BPM in de handelsberekening" value={eur(head.bpm)} strong />
          </div>

          <div>
            <label className="label-xs mb-1 block" htmlFor="bpm-override-amount">
              Nieuwe BPM in euro's
            </label>
            <div className="flex items-center gap-2">
              <span className="mono text-[14px] font-semibold text-slate-500">€</span>
              <input
                id="bpm-override-amount"
                data-testid="bpm-override-amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="numeric"
                className="mono h-9 w-40 rounded-sm border border-slate-300 bg-white px-2 text-[14px] font-semibold outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
              />
              {preview?.current_bpm_low !== null && preview?.current_bpm_low !== undefined && (
                <span className="text-[11px] text-slate-500">
                  huidige BPM-range groep: {eur(preview.current_bpm_low)} –{" "}
                  {eur(preview.current_bpm_high)}
                </span>
              )}
            </div>
          </div>

          <div className="border border-slate-200">
            <div className="border-b border-slate-200 bg-slate-100 px-3 py-1.5">
              <span className="label-xs">Technische voertuiggroep</span>
              <div
                className="mt-0.5 text-[12px] font-semibold text-slate-800"
                data-testid="bpm-override-group-label"
              >
                {preview?.group_label || "—"}
              </div>
            </div>
            <div className="flex flex-wrap gap-1 px-3 py-2">
              {(preview?.criteria || []).map((c) => (
                <span
                  key={c.label}
                  className="chip border-slate-300 bg-slate-50 text-[10px] text-slate-700"
                >
                  {c.label}: {c.value}
                </span>
              ))}
            </div>
            <div className="border-t border-slate-100 px-3 py-2 text-[12px] text-slate-700">
              Deze wijziging wordt toegepast op{" "}
              <b data-testid="bpm-override-affected-count">{count}</b> technisch overeenkomstige
              voertuig(en).
            </div>
            {busy === "preview" ? (
              <div className="flex items-center gap-2 px-3 py-2 text-[12px] text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Overeenkomstige auto's zoeken…
              </div>
            ) : (
              <div className="max-h-44 overflow-y-auto" data-testid="bpm-override-affected-list">
                {(preview?.affected || []).map((item) => (
                  <div
                    key={item.listing_id}
                    className={`flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-1.5 text-[11px] ${
                      item.is_selected ? "bg-blue-50" : ""
                    }`}
                  >
                    <span className="min-w-0 truncate text-slate-700">
                      {item.title}
                      {item.is_selected && (
                        <span className="ml-1 text-[10px] font-semibold text-blue-700">
                          (geselecteerd)
                        </span>
                      )}
                    </span>
                    <span className="mono shrink-0 text-slate-600">
                      {dateOnly(item.first_registration)} · {km(item.mileage_km, { fallback: "—" })}{" "}
                      · BPM {eur(item.current_bpm, { fallback: "onbekend" })}
                      {item.manual ? " (handmatig)" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {preview?.mixed_registrations && (
            <div className="space-y-1.5">
              <InlineWarning tone="warning" testid="bpm-override-mixed-warning">
                {preview.warning} Data eerste toelating in deze groep:{" "}
                {preview.registration_dates.join(", ")}.
              </InlineWarning>
              <label className="flex items-center gap-2 text-[12px] font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={confirmMixed}
                  data-testid="bpm-override-confirm-mixed"
                  onChange={(e) => setConfirmMixed(e.target.checked)}
                  className="h-3.5 w-3.5 accent-blue-600"
                />
                Ja, {eur(valid ? value : 0)} als vaste BPM toepassen op alle geselecteerde
                voertuigen
              </label>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => apply("GROUP")}
              disabled={!valid || busy !== ""}
              data-testid="bpm-override-apply-group"
              className="flex items-center gap-1.5 rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-blue-700 disabled:opacity-50"
            >
              {busy === "GROUP" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Toepassen op alle {count}
            </button>
            <button
              type="button"
              onClick={() => apply("SINGLE")}
              disabled={!valid || busy !== ""}
              data-testid="bpm-override-apply-single"
              className="flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100 disabled:opacity-50"
            >
              {busy === "SINGLE" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Alleen deze auto
            </button>
            {(isManual || preview?.existing_override) && (
              <>
                <button
                  type="button"
                  onClick={() => restore("SINGLE")}
                  disabled={busy !== ""}
                  data-testid="bpm-override-restore-single"
                  className="flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Automatische BPM — deze auto
                </button>
                <button
                  type="button"
                  onClick={() => restore("GROUP")}
                  disabled={busy !== ""}
                  data-testid="bpm-override-restore-group"
                  className="flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Automatische BPM — hele groep
                </button>
              </>
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            Na bevestiging worden landed cost, bijdrage, marge, ROI, Deal Score en de rangschikking
            direct opnieuw berekend. Een handmatige BPM heeft altijd voorrang op de automatische
            CO₂-berekening en blijft bewaard voor volgende scans.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
